import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { createEventReservationPreview, getReservationConfirmation } from '@/lib/api.ts';
import { ApiError } from '@/lib/ApiError.ts';
import { getApiErrorCode } from '@/lib/apiErrorCode.ts';
import type {
  ConfirmationResponseReservation,
  EventReservationData,
  Post_Reserve,
} from '@/lib/types.ts';
import { getRestaurantImage } from '@/lib/types.ts';
import { AuthAtom, useRestaurantId, useWidgetState } from '../state.ts';
import { getActiveEventId, getSelectedTicketId } from '../eventFlow.ts';

export interface BookingPreviewRestaurant {
  id: number;
  name: string;
  address?: string;
  neighborhood_name?: string;
  squarish_crop_image_url?: string;
  wide_crop_image_url?: string;
  show_merchandising_tax_and_tip?: boolean;
  show_merchandising_price_totals?: boolean;
  merchandising_policy?: string | null;
  merchandising_policy_title?: string | null;
  merchandising_policy_opt_in_label?: string | null;
  merchandising_marketing_opt_in_label?: string | boolean;
  expanded_tc?: string | null;
  expanded_tc_title?: string | null;
  expanded_tc_opt_in_label?: string | null;
  disclosure_text?: string | null;
  disclosure_link_url?: string | null;
  disclosure_link_label?: string | null;
}

export interface BookingPreviewItem {
  name?: string;
  image?: string;
  quantity?: number;
  price__currency?: string;
}

export interface BookingPreview {
  requires_pmc_checkout?: boolean;
  restaurant: BookingPreviewRestaurant;
  items: BookingPreviewItem[];
  party_size: number;
  date_formatted: string;
  time_formatted: string;
  fee_restaurant: number;
  fee_restaurant__currency: string;
  tax_rate: number;
  tax_amount: number;
  tax_amount__currency?: string;
  service_charges: Array<{ label: string; amount__currency: string }>;
  total_paid: number;
  total_paid__currency: string;
  credits_used__currency?: string;
  credits_used?: number;
  points_used_value?: number;
  points_used_currency_value__currency?: string;
  table_type?: string | null;
  notices?: string[];
  cancellation_policies?: string[];
  price_per_person?: number;
  price_per_person__currency?: string;
  /** Built reserve_body ready for createReservation (restaurant bookings) */
  reserve_body?: Post_Reserve;
}

function mapConfirmationToPreview(
  confirmation: ConfirmationResponseReservation,
  paymentMethodId?: string
): BookingPreview {
  const rest = confirmation.restaurant;
  const time = extractTime(confirmation.date_full);
  return {
    restaurant: {
      id: rest.id,
      name: rest.name,
      address: rest.address,
      neighborhood_name: rest.neighborhood_name,
      squarish_crop_image_url: rest.squarish_crop_image_url,
      wide_crop_image_url: rest.wide_crop_image_url,
      show_merchandising_tax_and_tip: rest.show_merchandising_tax_and_tip,
      show_merchandising_price_totals: rest.show_merchandising_price_totals,
      merchandising_policy: rest.merchandising_policy,
      merchandising_policy_title: rest.merchandising_policy_title,
      merchandising_policy_opt_in_label: rest.merchandising_policy_opt_in_label,
      merchandising_marketing_opt_in_label: rest.merchandising_marketing_opt_in_label,
      expanded_tc: rest.expanded_tc,
      expanded_tc_title: rest.expanded_tc_title,
      expanded_tc_opt_in_label: rest.expanded_tc_opt_in_label,
      disclosure_text: rest.disclosure_text,
      disclosure_link_url: rest.disclosure_link_url,
      disclosure_link_label: rest.disclosure_link_label,
    },
    items: [
      {
        name: confirmation.table_type ?? undefined,
        image: getRestaurantImage(rest),
        quantity: confirmation.party,
        price__currency: confirmation.price_per_person__currency,
      },
    ],
    party_size: confirmation.party,
    date_formatted: dayjs(confirmation.date_full).isValid()
      ? dayjs(confirmation.date_full).format('ddd, MMMM D')
      : confirmation.date_full,
    time_formatted: dayjs(confirmation.date_full).isValid()
      ? dayjs(confirmation.date_full).format('h:mm A')
      : confirmation.time,
    fee_restaurant: confirmation.fee_restaurant,
    fee_restaurant__currency: confirmation.fee_restaurant__currency,
    tax_rate: confirmation.tax_rate,
    tax_amount: confirmation.tax_amount,
    tax_amount__currency: confirmation.tax_amount__currency,
    service_charges: (confirmation.service_charges__currency ?? []).map((sc) => ({
      label: sc.label,
      amount__currency: sc.amount,
    })),
    total_paid: confirmation.total,
    total_paid__currency: confirmation.total__currency,
    credits_used__currency: confirmation.credits_used__currency,
    credits_used: confirmation.credits_used,
    points_used_value: confirmation.points_used,
    points_used_currency_value__currency: confirmation.points_used_currency_value__currency,
    table_type: confirmation.table_type,
    notices: confirmation.notices,
    cancellation_policies: confirmation.cancellation_policies,
    price_per_person: confirmation.price_per_person,
    price_per_person__currency: confirmation.price_per_person__currency,
    reserve_body: {
      restaurant: rest.id,
      seating: confirmation.seating.id,
      seats: confirmation.party,
      date: confirmation.date_full,
      confirmed_price: confirmation.price_per_person,
      skip_level_check: true,
      // Flex slots (slot_time_end set on the api side) reject any reservation
      // payload that doesn't carry the user-picked time. We extract the H:i:s
      // the user actually picked from `date_full` so flex slots get a valid
      // time-within-window; single-time slots derive time from the slot and
      // ignore this field. Pulling via regex avoids any timezone
      // reinterpretation that `dayjs(date_full)` would do on a `+00:00`-
      // suffixed local string.
      ...(time ? { time } : {}),
      ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
    },
  };
}

/**
 * Pull the H:i:s segment out of a `date_full` string regardless of timezone
 * notation. Returns undefined if the string lacks a recognizable time so the
 * caller can decide whether to omit the field.
 *
 * The match is anchored on the ISO 8601 date/time separator (`T` or space) to
 * avoid catching colon-delimited timezone offsets like `+05:30:00` that could
 * otherwise win on the unanchored pattern and silently book the wrong slot.
 */
function extractTime(dateFull: string | null | undefined): string | undefined {
  if (!dateFull) {
    return undefined;
  }

  const match = dateFull.match(/[T ](\d{2}:\d{2}:\d{2})/);

  return match?.[1];
}

function mapEventPreviewToPreview(data: EventReservationData): BookingPreview {
  const rest = data.restaurant;
  return {
    requires_pmc_checkout: data.requires_pmc_checkout,
    restaurant: {
      id: rest?.id ?? 0,
      name: rest?.name ?? '',
      address: rest?.address,
      neighborhood_name: rest?.neighborhood_name,
      wide_crop_image_url: rest?.wide_crop_image_url,
      show_merchandising_tax_and_tip: rest?.show_merchandising_tax_and_tip,
      show_merchandising_price_totals: rest?.show_merchandising_price_totals,
      merchandising_policy: rest?.merchandising_policy,
      merchandising_policy_title: rest?.merchandising_policy_title,
      merchandising_policy_opt_in_label: rest?.merchandising_policy_opt_in_label,
      merchandising_marketing_opt_in_label: rest?.merchandising_marketing_opt_in_label,
      expanded_tc: rest?.expanded_tc,
      expanded_tc_title: rest?.expanded_tc_title,
      expanded_tc_opt_in_label: rest?.expanded_tc_opt_in_label,
      disclosure_text: rest?.disclosure_text,
      disclosure_link_url: rest?.disclosure_link_url,
      disclosure_link_label: rest?.disclosure_link_label,
    },
    items: (data.items ?? []).map((item) => ({
      name: item.item?.name,
      image: item.item?.image,
      quantity: item.quantity,
      price__currency: item.price__currency,
    })),
    party_size: data.party_size ?? 0,
    date_formatted: data.event?.datetime_local__formatted?.date_long ?? data.event?.date ?? '',
    time_formatted: data.event?.datetime_local__formatted?.time ?? data.event?.time ?? '',
    fee_restaurant: data.fee_restaurant ?? 0,
    fee_restaurant__currency: data.fee_restaurant__currency ?? '',
    tax_rate: data.tax_rate ?? 0,
    tax_amount: data.tax_amount ?? 0,
    tax_amount__currency: data.tax_amount__currency,
    service_charges: (data.service_charges__currency ?? []).map((sc) => ({
      label: sc.label,
      amount__currency: sc.amount,
    })),
    total_paid: data.total_paid ?? 0,
    total_paid__currency: data.total_paid__currency ?? '',
    credits_used__currency: data.credits_used__currency,
    credits_used: data.credits_used,
    points_used_value: data.points_used_value,
    points_used_currency_value__currency: data.points_used_currency_value__currency,
  };
}

/**
 * Fetches a booking preview from the correct API based on widget.bookingType.
 * - 'restaurant' → getReservationConfirmation (REST)
 * - 'event' → createEventReservationPreview (GraphQL)
 *
 * Returns a unified BookingPreview shape or null while loading.
 */
export function useBookingPreview(enabled = true): {
  preview: BookingPreview | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
} {
  const [widget, updateWidget] = useWidgetState();
  const auth = useAtomValue(AuthAtom);
  const id = useRestaurantId();
  const [preview, setPreview] = useState<BookingPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current || !auth?.token || !id) {
      return;
    }

    if (widget.bookingType === 'restaurant') {
      if (!widget.confirm_body) {
        setLoading(false);
        return;
      }

      fetchedRef.current = true;

      const fetchRestaurantPreview = async () => {
        try {
          const response = await getReservationConfirmation(auth.token, id, widget.confirm_body!);
          const mapped = mapConfirmationToPreview(response.reservation, widget.payment_method);
          setPreview(mapped);

          // Also build reserve_body for the checkout step
          updateWidget({
            reserve_body: mapped.reserve_body,
          });
        } catch (e) {
          Sentry.captureException(e);
          setErrorCode(getApiErrorCode(e));
          setError('Unable to load pricing. Please go back and try again.');
        } finally {
          setLoading(false);
        }
      };

      void fetchRestaurantPreview();
    } else if (widget.bookingType === 'event') {
      const eventId = getActiveEventId(widget);
      const ticketId = getSelectedTicketId(widget);

      if (!eventId || !ticketId || !widget.confirm_body?.restaurant_id) {
        setLoading(false);
        return;
      }

      fetchedRef.current = true;

      const fetchEventPreview = async () => {
        try {
          const quantity = Math.max(widget.eventInfo?.quantity ?? 1, 1);
          const ticketItems = Array.from({ length: quantity }, () => ticketId);
          const addOnItems = widget.eventInfo?.addOnItemIds ?? [];
          const items = [...ticketItems, ...addOnItems];

          const response = await createEventReservationPreview(
            auth.token,
            { items },
            eventId,
            widget.confirm_body!.restaurant_id
          );

          if (response?.data) {
            const mapped = mapEventPreviewToPreview(response.data);
            setPreview(mapped);

            // Update widget with enriched event data from preview
            if (response.data.event) {
              updateWidget({
                event: {
                  ...widget.event,
                  id: eventId,
                  date: response.data.event.date,
                  name: response.data.event.name,
                  datetime_local__formatted: response.data.event.datetime_local__formatted,
                  time:
                    response.data.event.datetime_local__formatted?.time ?? response.data.event.time,
                },
                eventInfo: {
                  ...widget.eventInfo,
                  id: eventId,
                  fee_restaurant: response.data.fee_restaurant,
                  fee_restaurant__currency: response.data.fee_restaurant__currency,
                  items: response.data.items,
                  party_size: response.data.party_size,
                  restaurant: response.data.restaurant,
                  service_charges: response.data.service_charges,
                  service_charges__currency: response.data.service_charges__currency,
                  tax_amount: response.data.tax_amount,
                  tax_amount__currency: response.data.tax_amount__currency,
                  tax_rate: response.data.tax_rate,
                  total_paid: response.data.total_paid,
                  total_paid__currency: response.data.total_paid__currency,
                  credits_used: response.data.credits_used,
                  credits_used__currency: response.data.credits_used__currency,
                  points_used_value: response.data.points_used_value,
                  points_used_currency_value__currency:
                    response.data.points_used_currency_value__currency,
                },
              });
            }
          } else {
            const apiMessage = (response as unknown as Record<string, unknown>)?.message;
            const errorText =
              typeof apiMessage === 'string' && apiMessage
                ? apiMessage
                : 'Unable to load event pricing. Please go back and try again.';
            setError(errorText);
            Sentry.captureMessage('Event preview returned no data', {
              extra: {
                eventId,
                ticketId,
                restaurantId: widget.confirm_body!.restaurant_id,
                response,
              },
            });
          }
        } catch (e: unknown) {
          Sentry.captureException(e);
          setErrorCode(getApiErrorCode(e));
          const apiMsg =
            e instanceof ApiError ? (e.response as Record<string, unknown>)?.message : undefined;
          const errText =
            typeof apiMsg === 'string' && apiMsg
              ? apiMsg
              : 'Unable to load pricing. Please go back and try again.';
          setError(errText);
        } finally {
          setLoading(false);
        }
      };

      void fetchEventPreview();
    } else {
      // No bookingType set — shouldn't reach payment/checkout
      setLoading(false);
    }
  }, [enabled, auth?.token, id, widget.bookingType, widget.confirm_body, widget.payment_method]);

  return { preview, loading, error, errorCode };
}
